/*
 * Authors: mariushirjanu
 * Created: 23/03/2023
 */
import _ from 'underscore';
import { IContact, ICountryExtended, ILocationExtended, IState } from '@type_definitions/datatypes';
import { BillingType } from '../order-entry-data.service';
import { IInvoiceAddress, ILocationOption, InvoiceAddressType, InvoiceContactType, IOrderEntryContact, IShippingAddress, OrderEntryAddressType, OrderEntryContactType, ShippingAddressType, ShippingContactType } from './order-entry-location-data.service';
import { OrderEntryLocationService } from './order-entry-location.service';

export interface IAddressOption {
  id:InvoiceAddressType | ShippingAddressType;
  type?:string;
  default?:boolean;
}

export interface IContactOption {
  id:InvoiceContactType | ShippingContactType;
  type?:string;
  default?:boolean;
  disabled?:boolean;
}


export interface IAddressData {
  location:ILocationExtended;
  searchCountry?:ICountryExtended;
  searchState?:IState;
  country:ICountryExtended;
  vatName:string;
  office:string;
  defaultAddress:boolean;
  company:string;
  address:string;
  zip:string;
  state:IState;
  city:string;
  tax:string;
  invoiceEmailAddress:string;
}

export interface IContactModel {
  contact:IContact;
  id?:number;
  fullName:string;
  email:string;
  directPhone:string;
}

export interface IBillingType {
  id:BillingType;
  name?:string;
}

export class BaseOrderEntryLocationToService {
  // data for address type
  private _addressType:IAddressOption;
  private _addressTypeOptions:IAddressOption[];
  private _loadedAddresses:IInvoiceAddress[] | IShippingAddress[]; // contains the list of loaded addresses for each address type
  // data for address
  private _addressData:IAddressData; // contains the address data of the order loaded from the server / used to be sent to the server (this does not change during UI address updates; only has to be changed after saving the order)
  private _addressModels:IAddressData[] = []; // keeps an array of address info for all possible address types (includes IAddressModel + IInvoiceAddress/IShippingAddress)
  // data for location field (existing location)
  private _locationsOptions:ILocationOption[]; // contains the full list of loaded location options

  // data for contact type
  private _contactType:IContactOption; //
  private _contactTypeOptions:IContactOption[]; // this is a limited object of _loadedContacts, needed only for rendering radio buttons options
  private _loadedContacts:IOrderEntryContact[]; // contains the list of loaded contacts for each contact type
  // data for contact
  private _contactData:IContactModel; // contains the contact data loaded of the order from the server / used to be sent to the server (this does not change during UI address updates; only has to be changed after saving the order)
  private _contactModels:IContactModel[] = []; // keeps an array of contact info for all possible contact types

  // other data
  private _comments:string;
  private _overnight:boolean;
  private _billingType:IBillingType;
  private _eInvoiceMailAddress:string;
  private _eInvoiceMailAddressEditable:boolean;

  constructor(
    protected orderEntryLocationService:OrderEntryLocationService
  ) {
    /***/
  }

  /**
   * ---- Address ----
   */

  public get selectedAddressType():IAddressOption {
    return this._addressType;
  }
  public set selectedAddressType(addressType:IAddressOption) {
    this._addressType = addressType;
  }

  public get addressTypeOptions():IAddressOption[] {
    return this._addressTypeOptions;
  }
  public setAddressTypeOptions(loadedAddresses:IInvoiceAddress[] | IShippingAddress[], allowToAddCurrentAddress?:boolean):void {
    allowToAddCurrentAddress = allowToAddCurrentAddress && this.shouldAddCurrentAddress;
    this._loadedAddresses = loadedAddresses;

    this._addressTypeOptions = [];

    _.each(loadedAddresses, (loadedAddress:IInvoiceAddress | IShippingAddress) => {
      this._addressTypeOptions.push({
        id: loadedAddress.type,
        type: `ORDERENTRY.LOCATION.ADDRESS.${loadedAddress.type}`,
        default: allowToAddCurrentAddress ? false : loadedAddress.default
      });
    });

    // only add the current address if an address type has been selected
    if (allowToAddCurrentAddress) {
      this._addressTypeOptions.unshift({
        id: OrderEntryAddressType.CURRENT_ADDRESS,
        type: `ORDERENTRY.LOCATION.ADDRESS.${OrderEntryAddressType.CURRENT_ADDRESS}`,
        default: true
      });
    }
  }

  // get address info for any addressType
  public getAddress(addressType:InvoiceAddressType | ShippingAddressType):IAddressData {
    if(!addressType){
      return <any>{};
    }
    return this._addressModels && this._addressModels[addressType];
  }
  // set address info for any addressType
  public setAddress(addressType:InvoiceAddressType | ShippingAddressType, addressData:IAddressData){
    this._addressModels[addressType] = addressData;
  }

  // get address info for the current selected addressType
  public get selectedAddress():IAddressData {
    if(!this.selectedAddressType?.id){
      return <any>{};
    }
    return this.getAddress(this.selectedAddressType.id);
  }
  // set address info for the current selected contactType
  public set selectedAddress(addressData:IAddressData){
    if(!this.selectedAddressType?.id){
      return;
    }
    this.setAddress(this.selectedAddressType.id, addressData);
  }

  // get the order address data loaded from the server
  public get addressData():IAddressData {
    return this._addressData;
  }
  // set the order address data loaded from the server
  public set addressData(address:IAddressData) {
    this._addressData = address;
  }

  // this returns the loaded address for the provided addressType
  public getLoadedAddress(addressType:InvoiceAddressType | ShippingAddressType): IInvoiceAddress | IShippingAddress {
    return _.find(this._loadedAddresses, (loadedAddress:IInvoiceAddress | IShippingAddress) => loadedAddress.type === addressType);
  }

  // set the all loaded address locations
  public set locationOptions(locationOptions:ILocationOption[]) {
    this._locationsOptions = locationOptions;
  }
  // get the location option based on the location id
  public getLocationOption(locationId:number):ILocationOption {
    return _.find(this._locationsOptions, (loadedAddress:ILocationOption) => loadedAddress.location.id === locationId);
  }

  // do not add current address if there is no addressType or no location (ignore companyCountry and invoiceEmailAddress)
  private get shouldAddCurrentAddress():boolean {
    const loadedLocation:ILocationExtended = _.clone(this.addressData?.location);
    delete loadedLocation?.companyCountry;
    delete loadedLocation?.invoiceEmailAddress;
    return !!this.selectedAddressType && !_.isEmpty(loadedLocation);
  }

  /**
   * ---- Contact ----
   */

  public get contactType():IContactOption {
    return this._contactType;
  }
  public set contactType(contactType:IContactOption) {
    this._contactType = contactType;

    // when contactType changes -> reselect the contact
    this.selectedContact = this.getContact(contactType?.id);
  }

  public get contactTypeOptions():IContactOption[] {
    return this._contactTypeOptions;
  }
  public setContactTypeOptions(loadedContacts:IOrderEntryContact[], addCurrent?:boolean, requesterRequired?:boolean):void {
    this._loadedContacts = loadedContacts;
    this._contactTypeOptions = [];

    _.each(loadedContacts, (data:IOrderEntryContact) => {
      const option:IContactOption = <any>{};
      option.id = data.type;
      if (data.type === OrderEntryContactType.MY_CONTACT && requesterRequired) {
        option.type = 'ORDERENTRY.LOCATION.CONTACT.REQUESTER_CONTACT';
      } else {
        option.type = `ORDERENTRY.LOCATION.CONTACT.${data.type}`;
      }

      this._contactTypeOptions.push(option);

      // when the contact type contains a contact and contact model is empty -> set the contact to the model
     if(data?.contact?.id && _.isEmpty(this.selectedContact?.contact)) {
       const contact:IContact = _.clone(data.contact);
       this.setContact(data.type, this.orderEntryLocationService.formatContact(contact));
     }
    });

    if (addCurrent) {
      this._contactTypeOptions.unshift({
        id: OrderEntryContactType.CURRENT_CONTACT,
        type: `ORDERENTRY.LOCATION.CONTACT.${OrderEntryContactType.CURRENT_CONTACT}`,
        default: true
      });
    }
  }

  // get contact info for the current selected contactType
  public get selectedContact():IContactModel {
    if(!this.contactType?.id){
      return;
    }
    return this.getContact(this.contactType.id);
  }
  // set contact info for the current selected contactType
  public set selectedContact(contactData:IContactModel){
    if(!this.contactType?.id){
      return;
    }
    this.setContact(this.contactType.id, contactData);
  }

  // get contact info for any contactType
  public getContact(contactType:InvoiceContactType | ShippingContactType):IContactModel {
    return this._contactModels[contactType];
  }
  // set contact info for any contactType
  public setContact(contactType:InvoiceContactType | ShippingContactType, contactData:IContactModel){
    this._contactModels[contactType] = contactData;
  }

  // this returns the loaded contact for the provided contactType
  public getLoadedContact(contactType:InvoiceContactType | ShippingContactType): IOrderEntryContact {
    return _.find(this._loadedContacts, (loadedContact:IOrderEntryContact) => loadedContact.type === contactType);
  }

  // get the order contact data loaded from the server
  public get contactData():IContactModel {
    return this._contactData;
  }
  // set the order contact data loaded from the server
  public set contactData(contact:IContactModel) {
    this._contactData = contact;
  }

  /**
   * ---- Others ----
   */

  public get comments():string {
    return this._comments;
  }
  public set comments(comments:string) {
    this._comments = comments;
  }

  public get overnight():boolean {
    return this._overnight;
  }
  public set overnight(overnight:boolean) {
    this._overnight = overnight;
  }

  public get billingType():IBillingType {
    return this._billingType;
  }
  public set billingType(billingType:IBillingType) {
    this._billingType = billingType;
  }

  public get eInvoiceMailAddress():string {
    return this._eInvoiceMailAddress;
  }
  public set eInvoiceMailAddress(email:string) {
    this._eInvoiceMailAddress = email;
  }

  public get eInvoiceMailAddressEditable():boolean {
    return this._eInvoiceMailAddressEditable;
  }
  public set eInvoiceMailAddressEditable(editable:boolean) {
    this._eInvoiceMailAddressEditable = editable;
  }
  
  public clear():void {
    for (const key in this) {
      if (key.startsWith('_')) {
        this[ key ] = undefined;
      }
    }

    // make sure addressModels and contactModels are arrays again
    this._addressModels = [];
    this._contactModels = [];
  }
}